import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Button, ErrorState } from '@taskflow/ui';
import { ApiError } from '../services/httpClient';
import { en } from '../locales/en';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last line of defence against a white screen.
 *
 * A render error anywhere below this boundary produces a readable page with a
 * reference the user can quote, instead of a blank document that tells them
 * nothing and tells us nothing either.
 *
 * Deliberately a class component: `componentDidCatch` has no hook equivalent.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Phase 14 forwards this to Sentry. Until then the console is where a
    // developer will look, and the message must survive minification.
    console.error('[ErrorBoundary]', error.message, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const requestId = error instanceof ApiError ? error.requestId : undefined;

    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-base p-6">
        <div className="w-full max-w-md rounded-lg border border-border-subtle bg-surface-raised">
          <ErrorState
            title={en.errors.boundaryTitle}
            description={en.errors.boundaryBody}
            {...(requestId ? { requestId } : {})}
          />
          <div className="flex justify-center pb-6">
            <Button onClick={() => window.location.reload()}>{en.errors.reload}</Button>
          </div>
        </div>
      </div>
    );
  }
}
